// Dependencies
import { Component } from 'react';
import { connect } from 'react-redux';

import Icon from 'components/atoms/Icon';
import SearchGroup from 'components/atoms/SearchGroup';
import { SetSearchObjectAction } from 'store/actions/layoutActions';
import { OpenModal, PortType, RootState } from 'types';
import { FilterObjectType } from 'types/search';
import { SystemType } from 'types/system';
import { DateTimeFormat } from 'utils/dateTimeFormat';
import { checkDifferent, clone, setKey } from 'utils/formHandlers';
import { translate } from 'utils/translate';

import DateTag from 'components/atoms/DateTag';
import Input from 'components/atoms/Input';
import { bindActionCreators, Dispatch } from 'redux';
import { ShowMessageAction, showMessageAction } from 'store/actions/errorsActions';
import DateRangeShortcut from './DateRangeShortcut';

interface DateRangeFiltersComponentProps {
    openModelFunction: OpenModal;
    setFilterObject: SetSearchObjectAction;
    filterObject: FilterObjectType;
    defaultData: FilterObjectType;
    port: PortType;
    type: string;
    system: SystemType;
    withSortcut: boolean;
    dateTimeFormat: DateTimeFormat;
    searchObject: FilterObjectType;
    showMessageAction: ShowMessageAction;
}

interface DateRangeFiltersComponentState {
    filterObject: FilterObjectType;
    startDate: DateTimeFormat | null;
    endDate: DateTimeFormat | null;
    dateRange: string;
    lastDays: number | '';
}

class DateRangeFilters extends Component<DateRangeFiltersComponentProps, DateRangeFiltersComponentState> {
    constructor(props: DateRangeFiltersComponentProps) {
        super(props);
        this.state = {
            filterObject: clone(props.filterObject),
            startDate: null,
            endDate: null,
            dateRange: this.props.filterObject.date.dateRange,
            lastDays: '',
        };
    }

    static getDerivedStateFromProps(props: DateRangeFiltersComponentProps, state: DateRangeFiltersComponentState) {
        if (checkDifferent(props.filterObject, state.filterObject)) {
            return {
                filterObject: clone(props.filterObject),
                dateRange: props.filterObject.date.dateRange,
                lastDays: props.filterObject.date.lastDays,
            };
        }
        return null;
    }

    selectTime = (foucus) => {
        this.props.openModelFunction(
            'timeSelectModel',
            0,
            'medium',
            {
                startDate: this.state.filterObject?.date.start_utc.op,
                endDate: this.state.filterObject?.date.end_utc.op,
                filterObject: this.props.filterObject,
                foucus,
            },
            {
                onCancel: () => {
                    this.props.openModelFunction('timeSelectModel');
                },
                onConfirm: () => {
                    //console.log('saved');
                },
                setFilterObject: (data) => {
                    console.log(data);
                    if (this.props.type === 'highlight') {
                        if (
                            //@ts-ignore
                            this.props.searchObject.date.start_utc?.op?.toTimestamp() >
                                data.date.start_utc?.op.toTimestamp() ||
                            //@ts-ignore
                            this.props.searchObject.date.end_utc?.op?.toTimestamp() <
                                data.date.end_utc?.op?.toTimestamp()
                        ) {
                            this.props.showMessageAction({
                                messageType: 'ERROR',
                                message: 'The selected date range is outside the search date range.',
                            });
                            return;
                        }
                    }
                    this.props.setFilterObject(data);
                },
            }
        );
    };

    selectDate = (foucus) => {
        this.props.openModelFunction(
            'dateSelectModel',
            0,
            'custom',
            {
                startDate: this.state.filterObject?.date.start_utc.op,
                endDate: this.state.filterObject?.date.end_utc.op,
                startRange: this.props.system.date.startDate,
                endRange: this.props.system.date.endDate,
                today: this.props.system.date.today,
                filterObject: this.props.filterObject,
                dateRange: this.props.filterObject.date.dateRange,
                lastDays: this.props.filterObject.date.lastDays,
                foucus,
                type: this.props.type,
            },
            {
                onCancel: () => {
                    this.props.openModelFunction('dateSelectModel');
                },
                onConfirm: () => {
                    //console.log('saved');
                },
                setFilterObject: (data) => {
                    console.log(data);
                    if (this.props.type === 'highlight') {
                        if (
                            //@ts-ignore
                            this.props.searchObject.date.start_utc?.op?.toTimestamp() >
                                data.date.start_utc?.op?.toTimestamp() ||
                            //@ts-ignore
                            this.props.searchObject.date.end_utc?.op?.toTimestamp() <
                                data.date.end_utc?.op?.toTimestamp()
                        ) {
                            this.props.showMessageAction({
                                messageType: 'ERROR',
                                message: 'The selected date range is outside the search date range.',
                            });
                            return;
                        }
                    }
                    this.props.setFilterObject(data);
                },
            },
            true,
            920
        );
    };

    addDays = (value: number) => {
        const date = this.state.filterObject?.date.end_utc.op
            ? this.state.filterObject?.date.end_utc.op
            : this.props.system.date.endDate;

        const { startDate, endDate } = date.getDateFromDays(value === 0 ? 'activeToday' : 'activeDays', value, date);

        const filterObject = clone(this.props.filterObject);

        setKey(filterObject, 'date.end_utc.op', endDate);
        setKey(filterObject, 'date.start_utc.op', startDate);

        setKey(filterObject, 'date.lastDays', value);
        setKey(filterObject, 'date.dateRange', value === 0 ? 'activeToday' : 'activeDays');

        this.props.setFilterObject(filterObject);
        this.setState({
            dateRange: value === 0 ? 'activeToday' : 'activeDays',
        });

        return {
            perverse: filterObject.date.start_utc.op?.isAfter(this.props.system.date.startDate.date),
            next: filterObject.date.end_utc.op.isBefore(this.props.system.date.endDate.date),
            today: filterObject.date.start_utc.op.isSame(this.props.dateTimeFormat.parseDateTime().date),
        };
    };

    applayDate = (clear = false) => {
        if (clear) {
            this.setState({
                dateRange: '',
                lastDays: '',
                startDate: null,
                endDate: null,
            });
        }
        const filterObject = clone(this.props.filterObject);
        setKey(filterObject, 'date.lastDays', clear ? '' : this.state.lastDays);
        setKey(filterObject, 'date.dateRange', clear ? '' : this.state.dateRange);
        setKey(filterObject, 'date.end_utc.op', clear ? null : this.state.endDate?.clone());
        setKey(filterObject, 'date.start_utc.op', clear ? null : this.state.startDate?.clone());
        this.props.setFilterObject(filterObject);
    };

    chanageDateRange = (dateRange: string) => {
        if (dateRange === this.state.dateRange && dateRange !== 'lastDays') {
            this.applayDate(true);
            return;
        }

        const { startDate, endDate } = this.props.system.date.today.getDateFromDays(
            dateRange,
            dateRange === 'lastDays' ? this.state.lastDays || 1 : 0,
            this.props.system.date.today
        );

        if (this.props.type === 'highlight') {
            if (
                //@ts-ignore
                this.props.searchObject.date.start_utc.op?.toTimestamp() < startDate.toTimestamp() ||
                //@ts-ignore
                this.props.searchObject.date.end_utc.op?.toTimestamp() > endDate.toTimestamp()
            ) {
                this.props.showMessageAction({
                    messageType: 'ERROR',
                    message: 'The selected date range is outside the search date range.',
                });
                return;
            }
        }

        this.setState(
            {
                dateRange: dateRange,
                //@ts-ignore
                startDate: startDate,
                //@ts-ignore
                endDate: endDate,
            },

            this.applayDate
        );
    };

    render() {
        return (
            //add open attribute to SearchGroup component when add sections to search menu
            <SearchGroup open title={translate('Date range')}>
                <div className="flex flex-col gap-y-1 no-select ">
                    <div className="flex flex-wrap mt-2 gap-1">
                        <DateTag className="py-1 px-0" active={this.state.dateRange === 'thisWeek'}>
                            <div className="w-18 text-center" onClick={() => this.chanageDateRange('thisWeek')}>
                                {translate('this week')}
                            </div>
                        </DateTag>
                        <DateTag className="py-1 px-0" active={this.state.dateRange === 'lastWeek'}>
                            <div className="w-18 text-center" onClick={() => this.chanageDateRange('lastWeek')}>
                                {translate('last week')}
                            </div>
                        </DateTag>
                        <DateTag className="py-1 px-0" active={this.state.dateRange === 'thisMonth'}>
                            <div className="w-18 text-center" onClick={() => this.chanageDateRange('thisMonth')}>
                                {translate('this month')}
                            </div>
                        </DateTag>
                        <DateTag className="py-1 px-0" active={this.state.dateRange === 'lastMonth'}>
                            <div className="w-18 text-center" onClick={() => this.chanageDateRange('lastMonth')}>
                                {translate('last month')}
                            </div>
                        </DateTag>
                        <DateTag className="py-1 px-0" active={this.state.dateRange === 'thisYear'}>
                            <div className="w-18 text-center" onClick={() => this.chanageDateRange('thisYear')}>
                                {translate('this year')}
                            </div>
                        </DateTag>
                        <DateTag className="py-1 px-0" active={this.state.dateRange === 'lastYear'}>
                            <div className="w-18 text-center" onClick={() => this.chanageDateRange('lastYear')}>
                                {translate('last year')}
                            </div>
                        </DateTag>

                        <div
                            className={` ${
                                this.state.dateRange === 'lastDays' ? 'border' : 'border-0'
                            }  pb-1  relative w-18 text-xs rounded-full   date    mr-8 pt-1 items-center font-medium leading-4  cursor-pointer flex flex-row`}
                        >
                            <Input
                                value={
                                    this.state.dateRange === 'lastDays'
                                        ? //@ts-ignore
                                          this.state.lastDays
                                        : ''
                                }
                                name="lastDays"
                                placeholder={translate('Last days')}
                                onChange={(name: string, value: string) => {
                                    const isValidNumber = /^[1-9][0-9]{0,2}$/.test(value);
                                    console.log(value, isValidNumber);
                                    this.setState(
                                        {
                                            lastDays: isValidNumber ? parseInt(value) : this.state.lastDays,
                                        },
                                        () => {
                                            if (isValidNumber) {
                                                this.chanageDateRange('lastDays');
                                            }

                                            if (value === '') {
                                                this.applayDate(true);
                                            }
                                        }
                                    );
                                }}
                                className=" p-0 text-center bg-transparent h-4 font-medium block w-16 text-sm leading-4 border-0 "
                            ></Input>
                            {this.state.dateRange === 'lastDays' ? (
                                <div
                                    onClick={() => {
                                        this.applayDate(true);
                                    }}
                                    className="flex  items-center "
                                >
                                    <Icon
                                        viewBox="0 0 32 32 "
                                        className="w-2 h-2 m-1 cursor-pointer absolute right-1 "
                                        fill="none"
                                    >
                                        <path d="M2 30 L30 2 M30 30 L2 2" />
                                    </Icon>
                                </div>
                            ) : null}
                        </div>
                    </div>
                    <div className="flex flex-col gap-x-1 mt-3">
                        <div className="flex gap-1  justify-start">
                            <div
                                className="cursor-pointer"
                                onClick={() => {
                                    this.selectDate('startDate');
                                }}
                            >
                                <Icon className="text-primary-link w-4 h-4  ">
                                    <path d="M4 7a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-12z" />
                                    <path d="M16 3v4" />
                                    <path d="M8 3v4" />
                                    <path d="M4 11h16" />
                                    <path d="M8 14v4" />
                                    <path d="M12 14v4" />
                                    <path d="M16 14v4" />
                                </Icon>
                            </div>
                            <div
                                onClick={() => {
                                    this.selectDate('startDate');
                                }}
                                className="text-primary-link cursor-pointer"
                            >
                                {translate('Start date')}
                            </div>
                        </div>
                        <div className="flex font-bold  ">
                            <div
                                className="cursor-pointer"
                                onClick={() => {
                                    this.selectDate('startDate');
                                }}
                            >
                                {this.state.filterObject?.date.start_utc.op
                                    ? this.state.filterObject?.date.start_utc.op.formatDateTimeToString(
                                          'dd LLL yyyy HH:mm Z'
                                      )
                                    : this.props.system.date.startDate.formatDateTimeToString('dd LLL yyyy')}
                            </div>
                            {this.state.filterObject?.date.start_utc.op && (
                                <div
                                    onClick={() => {
                                        this.selectTime('startDate');
                                    }}
                                    className="flex items-center ml-3 cursor-pointer"
                                >
                                    <Icon className="w-4 h-4">
                                        <path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" />
                                        <path d="M12 12l3 -2" />
                                        <path d="M12 7v5" />
                                    </Icon>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex flex-col gap-x-1 mt-5">
                        <div className="flex gap-1  justify-start ">
                            <div
                                onClick={() => {
                                    this.selectDate('endDate');
                                }}
                                className="cursor-pointer"
                            >
                                <Icon className="text-primary-link  w-4 h-4  ">
                                    <path d="M4 7a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-12z" />
                                    <path d="M16 3v4" />
                                    <path d="M8 3v4" />
                                    <path d="M4 11h16" />
                                    <path d="M8 14v4" />
                                    <path d="M12 14v4" />
                                    <path d="M16 14v4" />
                                </Icon>
                            </div>
                            <div
                                onClick={() => {
                                    this.selectDate('endDate');
                                }}
                                className="text-primary-link cursor-pointer"
                            >
                                {translate('End date')}
                            </div>
                        </div>
                        <div className="flex font-bold ">
                            <div
                                className="cursor-pointer"
                                onClick={() => {
                                    this.selectDate('endDate');
                                }}
                            >
                                {this.state.filterObject?.date.end_utc.op
                                    ? this.state.filterObject?.date.end_utc.op.formatDateTimeToString(
                                          'dd LLL yyyy HH:mm Z'
                                      )
                                    : this.props.system.date.endDate.formatDateTimeToString('dd LLL yyyy')}
                            </div>
                            {this.state.filterObject?.date.end_utc.op && (
                                <div
                                    onClick={() => {
                                        this.selectTime('endDate');
                                    }}
                                    className="flex items-center ml-3 cursor-pointer"
                                >
                                    <Icon className="w-4 h-4">
                                        <path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" />
                                        <path d="M12 12l3 -2" />
                                        <path d="M12 7v5" />
                                    </Icon>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                {this.props.withSortcut ? (
                    <DateRangeShortcut
                        dateRange={this.state.dateRange}
                        lastDay={
                            this.state.filterObject?.date.end_utc.op
                                ? this.state.filterObject?.date.end_utc.op
                                : this.props.system.date.endDate
                        }
                        applayDate={this.applayDate}
                        addDays={this.addDays}
                    ></DateRangeShortcut>
                ) : (
                    <></>
                )}
            </SearchGroup>
        );
    }
}

const dateRangeFiltersStateToProps = (state: RootState) => ({
    openModelFunction: state.layout.openModelFunction,
    port: state.port.port,
    system: state.port.system,
    dateTimeFormat: state.port.dateTimeFormat,
    searchObject: state.layout.searchObject,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const dateRangeFiltersDispatchToProps = (dispatch: Dispatch) => ({
    showMessageAction: bindActionCreators(showMessageAction, dispatch),
});

export default connect(dateRangeFiltersStateToProps, dateRangeFiltersDispatchToProps)(DateRangeFilters);
